import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
  FiUpload, 
  FiFile, 
  FiDownload, 
  FiTrash2, 
  FiEye,
  FiRefreshCw,
  FiCheck,
  FiX,
  FiBarChart2,
  FiFileText,
  FiAlertCircle
} from 'react-icons/fi';
import ExcelProcessor, { ProcessedExcelData } from '../../utils/ExcelProcessor';
import ReportsService from '../../services/reportsService';
import { useToast } from '../../components/toast/toastProvider';
import './reports.css';

interface ProcessedFile {
  id: string;
  name: string;
  size: number;
  uploadedAt: string;
  processedAt?: string;
  status: 'uploading' | 'processing' | 'completed' | 'error';
  rowCount?: number;
  errorMessage?: string;
  downloadUrl?: string;
  preview?: any[];
  processedData?: ProcessedExcelData;
}

const ReportsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'uploads' | 'dashboard'>('dashboard');
  const { addToast } = useToast();
  
  // Dashboard state
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [dateRange, setDateRange] = useState<{start?: string; end?: string}>({});

  // File upload state
  const [uploadedFiles, setUploadedFiles] = useState<ProcessedFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<ProcessedFile | null>(null);
  const [processedData, setProcessedData] = useState<ProcessedExcelData | null>(null);
  const [selectedSheet, setSelectedSheet] = useState<string>('');
  const [showPreview, setShowPreview] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadDashboard = useCallback(async () => {
    setDashboardLoading(true);
    try {
      const overview = await ReportsService.getDashboardOverview({
        startDate: dateRange.start,
        endDate: dateRange.end
      });
      setDashboardData(overview);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      addToast({
        type: 'error',
        title: 'Failed to Load Dashboard',
        message: 'Could not fetch dashboard data',
        duration: 5000
      });
    } finally {
      setDashboardLoading(false);
    }
  }, [addToast, dateRange.end, dateRange.start]);

  // Load dashboard data on mount
  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  // Handle file upload using ExcelProcessor
  const handleFileUpload = async (files: FileList) => {
    const file = files[0];
    if (!file) return;

    // Validate file using ExcelProcessor
    const validation = ExcelProcessor.validateFile(file);
    if (!validation.valid) {
      alert(validation.error);
      return;
    }

    // Create file record
    const newFile: ProcessedFile = {
      id: `file-${Date.now()}`,
      name: file.name,
      size: file.size,
      uploadedAt: new Date().toISOString(),
      status: 'uploading'
    };

    setUploadedFiles(prev => [newFile, ...prev]);

    try {
      // Process file using ExcelProcessor
      await processExcelFileWithProcessor(file, newFile.id);
    } catch (error) {
      console.error('File processing error:', error);
      updateFileStatus(newFile.id, 'error', `Processing failed: ${error}`);
    }
  };

  // Process Excel file using ExcelProcessor
  const processExcelFileWithProcessor = async (file: File, fileId: string) => {
    updateFileStatus(fileId, 'processing');

    try {
      let processedData: ProcessedExcelData;

      // Check file type and process accordingly
      if (file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv')) {
        // Process CSV file
        const content = await readFileAsText(file);
        processedData = await ExcelProcessor.processCSV(content);
      } else {
        // Process Excel file
        processedData = await ExcelProcessor.processFile(file, {
          maxRows: 10000,
          skipEmptyRows: true,
          trimValues: true
        });
      }

      // Generate summary and quality analysis
      const summary = ExcelProcessor.generateSummary(processedData);

      console.log('Processing complete:', {
        data: processedData,
        summary: summary
      });

      // Update file status with processed data
      updateFileStatus(fileId, 'completed', undefined, {
        rowCount: processedData.summary.totalRows,
        downloadUrl: await generateDownloadUrl(processedData, file.name),
        processedData: processedData
      });

    } catch (error: any) {
      console.error('Processing error:', error);
      updateFileStatus(fileId, 'error', error.message || 'Processing failed');
    }
  };

  // Helper function to read file as text
  const readFileAsText = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as string || '');
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  };

  // Generate download URL for processed data
  const generateDownloadUrl = async (data: ProcessedExcelData, originalName: string): Promise<string> => {
    try {
      // Export processed data to Excel/CSV
      const exportedBlob = await ExcelProcessor.exportToExcel(data, originalName.replace(/\.[^/.]+$/, '_processed'));

      // Create object URL for download
      const url = URL.createObjectURL(exportedBlob);
      return url;
    } catch (error) {
      console.error('Export error:', error);
      return '#'; // Fallback URL
    }
  };

  // Update file status
  const updateFileStatus = (
    fileId: string, 
    status: ProcessedFile['status'], 
    errorMessage?: string,
    additionalData?: Partial<ProcessedFile>
  ) => {
    setUploadedFiles(prev => prev.map(file => 
      file.id === fileId 
        ? { 
            ...file, 
            status, 
            processedAt: status === 'completed' ? new Date().toISOString() : file.processedAt,
            errorMessage,
            ...additionalData
          }
        : file
    ));
  };

  // Handle file preview with real processed data
  const handlePreviewFile = async (file: ProcessedFile) => {
    if (file.status !== 'completed' || !file.processedData) return;

    setSelectedFile(file);
    setProcessedData(file.processedData);
    setSelectedSheet(file.processedData.sheets[0] || '');
    setShowPreview(true);
  };

  // Handle file download
  const handleDownloadFile = async (file: ProcessedFile) => {
    if (!file.processedData || !file.downloadUrl) return;

    try {
      // Create download link
      const link = document.createElement('a');
      link.href = file.downloadUrl;
      link.download = `${file.name.replace(/\.[^/.]+$/, '')}_processed.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download file');
    }
  };

  // Handle export to different formats
  const handleExportData = async (format: 'csv' | 'json' | 'excel') => {
    if (!processedData) return;

    try {
      let blob: Blob;
      let filename: string;

      switch (format) {
        case 'csv':
          blob = await ExcelProcessor.exportToExcel(processedData, selectedFile?.name || 'export');
          filename = `${selectedFile?.name || 'export'}_export.csv`;
          break;
        case 'json':
          const jsonData = JSON.stringify(processedData, null, 2);
          blob = new Blob([jsonData], { type: 'application/json' });
          filename = `${selectedFile?.name || 'export'}_export.json`;
          break;
        case 'excel':
          blob = await ExcelProcessor.exportToExcel(processedData, selectedFile?.name || 'export');
          filename = `${selectedFile?.name || 'export'}_export.xlsx`;
          break;
        default:
          return;
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Export error:', error);
      alert(`Failed to export as ${format.toUpperCase()}`);
    }
  };

  // Handle file delete
  const handleDeleteFile = (fileId: string) => {
    if (window.confirm('Are you sure you want to delete this file?')) {
      setUploadedFiles(prev => {
        const fileToRemove = prev.find(file => file.id === fileId);
        if (fileToRemove?.downloadUrl?.startsWith('blob:')) {
          URL.revokeObjectURL(fileToRemove.downloadUrl);
        }
        return prev.filter(file => file.id !== fileId);
      });

      if (selectedFile?.id === fileId) {
        setSelectedFile(null);
        setProcessedData(null);
        setShowPreview(false);
      }
    }
  };

  // Handle drag and drop
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files);
    }
  };

  // Format file size (using ExcelProcessor utility)
  const formatFileSize = (bytes: number): string => {
    return ExcelProcessor.formatFileSize(bytes);
  };

  // Format date
  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString();
  };

  // Get status icon
  const getStatusIcon = (status: ProcessedFile['status']) => {
    switch (status) {
      case 'uploading':
      case 'processing':
        return <FiRefreshCw className="spin" size={16} />;
      case 'completed':
        return <FiCheck size={16} color="#16a34a" />;
      case 'error':
        return <FiX size={16} color="#dc2626" />;
      default:
        return <FiFile size={16} />;
    }
  };

  // Export analytics report
  const handleExportReport = async (format: 'pdf' | 'excel') => {
    try {
      const blob = await ReportsService.exportReportPDF('overview', {
        startDate: dateRange.start,
        endDate: dateRange.end
      });

      // Trigger file download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().split('T')[0];
      link.download = `arqite-report-${timestamp}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      addToast({
        type: 'success',
        title: 'Report Exported',
        message: `Report has been downloaded as ${format.toUpperCase()}`,
        duration: 3000
      });
    } catch (error) {
      console.error('Failed to export report:', error);
      addToast({
        type: 'error',
        title: 'Export Failed',
        message: 'Failed to export report',
        duration: 5000
      });
    }
  };

  return (
    <div className="reports-page">
      <header className="reports-header">
        <h1>Reports & Analytics</h1>
        <p>View project analytics and upload Excel files to generate comprehensive reports</p>
      </header>

      {/* Tabs */}
      <div className="reports-tabs">
        <button 
          className={`tab-btn ${activeTab === 'dashboard' ? 'active' : ''}`}
          onClick={() => setActiveTab('dashboard')}
        >
          <FiBarChart2 />
          Dashboard
        </button>
        <button 
          className={`tab-btn ${activeTab === 'uploads' ? 'active' : ''}`}
          onClick={() => setActiveTab('uploads')}
        >
          <FiUpload />
          File Upload
        </button>
      </div>

      <div className="reports-content">
        <div className="tab-content">
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className="dashboard-section">
            {dashboardLoading ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading dashboard...</p>
              </div>
            ) : dashboardData ? (
              <>
                {/* Date Range Filter */}
                <div className="filter-controls">
                  <div className="date-inputs">
                    <input
                      type="date"
                      value={dateRange.start || ''}
                      onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
                      placeholder="Start Date"
                    />
                    <input
                      type="date"
                      value={dateRange.end || ''}
                      onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
                      placeholder="End Date"
                    />
                  </div>
                  <div className="action-buttons">
                    <button className="btn-secondary" onClick={() => loadDashboard()}>
                      <FiRefreshCw /> Refresh
                    </button>
                    <button className="btn-primary" onClick={() => handleExportReport('pdf')}>
                      <FiDownload /> Export PDF
                    </button>
                    <button className="btn-primary" onClick={() => handleExportReport('excel')}>
                      <FiDownload /> Export Excel
                    </button>
                  </div>
                </div>

                {/* Dashboard Overview */}
                <div className="dashboard-overview">
                  <div className="stat-card">
                    <div className="stat-label">Total Snags</div>
                    <div className="stat-value">{dashboardData.totalSnags || 0}</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-label">Critical Issues</div>
                    <div className="stat-value critical">{dashboardData.criticalCount || 0}</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-label">Resolved</div>
                    <div className="stat-value success">{dashboardData.resolvedCount || 0}</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-label">Pending</div>
                    <div className="stat-value warning">{dashboardData.pendingCount || 0}</div>
                  </div>
                </div>

                {/* Charts placeholder */}
                <div className="charts-section">
                  <div className="chart-container">
                    <h3>Snags by Status</h3>
                    <div className="chart-placeholder">
                      <p>Status chart will be displayed here</p>
                    </div>
                  </div>
                  <div className="chart-container">
                    <h3>Snags by Priority</h3>
                    <div className="chart-placeholder">
                      <p>Priority chart will be displayed here</p>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="empty-state">
                <FiAlertCircle size={48} />
                <p>Failed to load dashboard data</p>
              </div>
            )}
          </div>
        )}

        {/* Upload Tab and Modal */}
        
        {activeTab === 'uploads' && (
          <div className="upload-section-wrapper">
            {/* Upload Area */}
            <div className="upload-section">
              <div 
                className={`upload-area ${isDragging ? 'dragging' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <FiUpload size={48} />
                <h3>Upload Excel File</h3>
                <p>Drag and drop your Excel file here, or click to browse</p>
                <p className="supported-formats">Supports: .xlsx, .xls, .csv (Max 10MB)</p>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={(e) => e.target.files && handleFileUpload(e.target.files)}
                  style={{ display: 'none' }}
                />
              </div>
            </div>

            {/* Files List */}
            {uploadedFiles.length > 0 && (
              <div className="files-section">
            <h2>Uploaded Files</h2>
            <div className="files-list">
              {uploadedFiles.map(file => (
                <div key={file.id} className="file-item">
                  <div className="file-info">
                    <div className="file-icon">
                      <FiFileText size={24} />
                    </div>
                    <div className="file-details">
                      <h4>{file.name}</h4>
                      <p>{formatFileSize(file.size)} • {formatDate(file.uploadedAt)}</p>
                      {file.rowCount && (
                        <p className="row-count">{file.rowCount} rows processed</p>
                      )}
                      {file.processedData && (
                        <p className="sheets-count">{file.processedData.summary.totalSheets} sheets</p>
                      )}
                      {file.errorMessage && (
                        <p className="error-message">
                          <FiAlertCircle size={14} />
                          {file.errorMessage}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="file-status">
                    {getStatusIcon(file.status)}
                    <span className={`status-text status-${file.status}`}>
                      {file.status.charAt(0).toUpperCase() + file.status.slice(1)}
                    </span>
                  </div>

                  <div className="file-actions">
                    {file.status === 'completed' && (
                      <>
                        <button
                          className="btn preview-btn"
                          onClick={() => handlePreviewFile(file)}
                          title="Preview data"
                        >
                          <FiEye size={16} />
                        </button>
                        <button
                          className="btn download-btn"
                          onClick={() => handleDownloadFile(file)}
                          title="Download processed file"
                        >
                          <FiDownload size={16} />
                        </button>
                      </>
                    )}
                    <button
                      className="btn delete-btn"
                      onClick={() => handleDeleteFile(file.id)}
                      title="Delete file"
                    >
                      <FiTrash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        

        {/* Data Preview Modal with Enhanced Features */}
        {showPreview && processedData && (
          <div className="preview-modal-overlay" onClick={() => setShowPreview(false)}>
            <div className="preview-modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>
                  <FiBarChart2 size={24} />
                  Data Preview: {selectedFile?.name}
                </h2>
                <div className="header-actions">
                  <div className="export-buttons">
                    <button 
                      className="export-btn"
                      onClick={() => handleExportData('csv')}
                      title="Export as CSV"
                    >
                      CSV
                    </button>
                    <button 
                      className="export-btn"
                      onClick={() => handleExportData('json')}
                      title="Export as JSON"
                    >
                      JSON
                    </button>
                    <button 
                      className="export-btn"
                      onClick={() => handleExportData('excel')}
                      title="Export as Excel"
                    >
                      Excel
                    </button>
                  </div>
                  <button 
                    className="modal-close-btn"
                    onClick={() => setShowPreview(false)}
                  >
                    <FiX size={20} />
                  </button>
                </div>
              </div>

              <div className="modal-content">
                {/* Enhanced Summary with Data Quality */}
                <div className="data-summary">
                  <div className="summary-item">
                    <span>Total Sheets</span>
                    <strong>{processedData.summary.totalSheets}</strong>
                  </div>
                  <div className="summary-item">
                    <span>Total Rows</span>
                    <strong>{processedData.summary.totalRows}</strong>
                  </div>
                  <div className="summary-item">
                    <span>File Size</span>
                    <strong>{processedData.summary.fileSize}</strong>
                  </div>
                  <div className="summary-item">
                    <span>Processed At</span>
                    <strong>{formatDate(processedData.summary.processedAt)}</strong>
                  </div>
                </div>

                {/* Sheet Tabs */}
                <div className="sheet-tabs">
                  {processedData.sheets.map(sheet => (
                    <button
                      key={sheet}
                      className={`sheet-tab ${selectedSheet === sheet ? 'active' : ''}`}
                      onClick={() => setSelectedSheet(sheet)}
                    >
                      {sheet}
                      <span className="sheet-row-count">
                        ({processedData.data[sheet]?.length || 0} rows)
                      </span>
                    </button>
                  ))}
                </div>

                {/* Data Table */}
                {selectedSheet && processedData.data[selectedSheet] && (
                  <div className="data-table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          {processedData.headers[selectedSheet].map((header, index) => (
                            <th key={index}>{header}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {processedData.data[selectedSheet].slice(0, 50).map((row, rowIndex) => (
                          <tr key={rowIndex}>
                            {processedData.headers[selectedSheet].map((header, colIndex) => (
                              <td key={colIndex}>{row[header] || '-'}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {processedData.data[selectedSheet].length > 50 && (
                      <p className="table-note">
                        Showing first 50 rows of {processedData.data[selectedSheet].length} total rows
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        </div>
        )};
        </div>
      </div>
    </div>
  );
};
  

export default ReportsPage;